// material-ui
import { Box, Collapse, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

// third-party
import { Column, useFilters, useSortBy, useTable } from 'react-table';
import { HeaderSort, TablePagination } from '../third-party/custom/ReactTableCustom';
import { EmptyTable } from '../EmptyTable';
import { CSVExport, HidingSelect } from '../third-party/ReactTable';
import { LabelKeyObject } from 'react-csv/components/CommonPropTypes';
import { sizeByBreakpoint, textSizeByBreakpoint } from '../../utils/helper';
import React, { useEffect, useState } from 'react';

// ==============================|| REACT TABLE COMPONENT ||============================== //

interface ReactTableProps {
  columns: Column[];
  data: any[];
  dataCount: number;
  handlePageSize: (size: number) => void;
  handlePageChange: (direction: 'next' | 'prev') => void;
  pageSize: number;
  pageIndex: number;
  getHeaderProps?: (column: any) => any;
  top?: boolean;
  isLoading?: boolean;
  isHideColumn?: boolean;
  visibleColumns?: string[];
  returnVisibleItems?: (items: any[]) => void;
  hidePaginationOnLessData?: boolean;
  hidePagePerRow?: boolean;
  csvFileName?: string;
  hidePagination?: boolean;
  border?: boolean;
  expandedRow?: number | number[] | null;
  onToggleExpand?: (rowIndex: number) => void;
  renderExpandedRow?: (rowData: any) => React.ReactNode;
  lastTHWidth?: string;
  footer?: any;
  footerSx?: React.CSSProperties;
  fullFooterCellSx?: React.CSSProperties;
  footerCellSxByColumn?: Record<string, React.CSSProperties>;
  expandedRowPadding?: number | string;
  scrollableBody?: boolean;
  maxBodyHeight?: string | number;
  cursorBased?: boolean;
  onGotoFirst?: () => void;
}

const ReactTable = ({
  columns,
  data,
  dataCount,
  handlePageSize,
  handlePageChange,
  pageSize = 10,
  pageIndex = 0,
  getHeaderProps,
  top,
  isLoading = true,
  isHideColumn = false,
  visibleColumns,
  returnVisibleItems,
  hidePaginationOnLessData = false,
  hidePagePerRow = false,
  csvFileName = 'table-download.csv',
  hidePagination = false,
  border = true,
  expandedRow,
  onToggleExpand,
  renderExpandedRow,
  lastTHWidth,
  footer,
  footerSx,
  fullFooterCellSx,
  footerCellSxByColumn,
  expandedRowPadding = 3,
  scrollableBody = false,
  maxBodyHeight = 400,
  cursorBased = false,
  onGotoFirst
}: ReactTableProps) => {
  const theme = useTheme();
  const [childRowHovered, setChildRowHovered] = useState(false);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    // @ts-ignore
    setPageSize,
    setHiddenColumns,
    allColumns,
    state: { hiddenColumns }
  } = useTable(
    {
      columns,
      data,
      initialState: {
        // @ts-ignore
        pageIndex: 0,
        pageSize,
        hiddenColumns: columns
          .filter((col: Column<{}>) => visibleColumns?.includes(col.accessor as string))
          .map((col) => col.accessor) as string[]
      },
      disableSortBy: true
    },
    useFilters,
    useSortBy
  );

  // Synchronize hidden columns when visibleColumns prop changes
  useEffect(() => {
    if (visibleColumns) {
      const columnsToHide = columns
        .filter((col: Column<{}>) => visibleColumns?.includes(col.accessor as string))
        .map((col) => col.accessor) as string[];
      setHiddenColumns(columnsToHide);
    }
  }, [visibleColumns, columns, setHiddenColumns]);

  // ============================== CSV Header ============================== //
  const headers: LabelKeyObject[] = allColumns
    .filter((col) => !hiddenColumns?.includes(col.id))
    .map((col) => ({ label: col.Header as string, key: col.id }));

  const shouldShowPagination = !hidePagination && !top && !(hidePaginationOnLessData && dataCount <= pageSize);

  // ============================== Styles ============================== //
  const tableHeaderStyles = {
    paddingX: '12px',
    paddingY: 0,
    fontSize: textSizeByBreakpoint(14),
    fontWeight: 600,
    color: '#6B7580',
    lineHeight: '20px',
    border: 'none !important',
    backgroundColor: 'transparent !important',
    textTransform: 'capitalize',
    '&::before': { display: 'none' },
    '&::after': { display: 'none' }
  };

  const tableCellStyles = {
    paddingX: '12px',
    paddingY: sizeByBreakpoint(1, 12),
    fontSize: textSizeByBreakpoint(14),
    fontWeight: 600,
    borderTop: '1px solid',
    borderBottom: '1px solid',
    borderColor: border ? `${theme.palette.border.main} !important` : 'transparent !important',
    whiteSpace: 'nowrap'
  };

  // ============================== Render ============================== //
  return (
    <Stack>
      {/* Top Pagination */}
      {top && !hidePagination && (
        <Box sx={{ p: 2 }}>
          <TablePagination
            handlePageSize={handlePageSize}
            gotoPage={handlePageChange}
            rows={rows}
            dataCount={dataCount}
            setPageSize={setPageSize}
            pageIndex={pageIndex}
            pageSize={pageSize}
            cursorBased={cursorBased}
            onGotoFirst={onGotoFirst}
          />
        </Box>
      )}

      {/* Column Visibility + CSV Export */}
      {isHideColumn && (
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ p: 2 }}>
          <HidingSelect
            hiddenColumns={hiddenColumns!}
            setHiddenColumns={setHiddenColumns}
            allColumns={allColumns}
            returnVisibleItems={returnVisibleItems}
          />
          <CSVExport data={data} filename={csvFileName} headers={headers} />
        </Stack>
      )}

      {/* Table */}
      <Box sx={{ 
        width: '100%', 
        overflowX: 'auto',
        ...(scrollableBody && {
          maxHeight: maxBodyHeight,
          overflowY: 'auto',
          flex: 1
        })
      }}>
        <Table {...getTableProps()} sx={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
        {/* Table Head */}
        <TableHead sx={{ backgroundColor: 'transparent !important' }}>
          {headerGroups.map((headerGroup, index) => (
            <TableRow {...headerGroup.getHeaderGroupProps()} sx={{ backgroundColor: 'transparent !important' }}>
              {headerGroup.headers.map((column: any, idx: number) => (
                <TableCell
                  key={column.id}
                  {...column.getHeaderProps([{ className: column.className }, getHeaderProps?.(column)])}
                  sx={{
                    ...tableHeaderStyles,
                    ...(idx === headerGroup.headers.length - 1 && {
                      pr: '24px !important',
                      whiteSpace: 'nowrap',
                      width: lastTHWidth ?? '1%'
                    })
                  }}
                >
                  <HeaderSort column={{ ...column, disableSortBy: true }} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>

        {/* Table Body */}
        <TableBody {...getTableBodyProps()}>
          {rows.length > 0 || isLoading ? (
            rows.map((row: any) => {
              prepareRow(row);
              const isExpanded = Array.isArray(expandedRow) 
                ? expandedRow.includes(row.index) 
                : expandedRow === row.index;

              return (
                <React.Fragment key={row.id ?? row.original?.id ?? row.index}>
                  <TableRow
                    {...row.getRowProps()}
                    sx={{
                      backgroundColor:
                        childRowHovered && isExpanded
                          ? theme.palette.mode === 'dark'
                            ? theme.palette.background.default
                            : `${alpha(theme.palette.primary[50] as string, 0.6)} !important`
                          : theme.palette.background.default,
                      '&:hover': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05) !important'
                            : `${alpha(theme.palette.primary[50] as string, 0.6)} !important`
                      },
                      '&:hover + tr': {
                        backgroundColor:
                          isExpanded &&
                          (theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.05) !important'
                            : `${alpha(theme.palette.primary[50] as string, 0.6)} !important`)
                      }
                    }}
                  >
                    {row.cells.map((cell: any, idx: number) => (
                      <TableCell
                        key={cell.column.id || idx}
                        {...cell.getCellProps([{ className: cell.column.className }])}
                        sx={{
                          ...tableCellStyles,
                          ...(idx === 0 && {
                            pl: '16px',
                            borderLeft: '1px solid',
                            borderTopLeftRadius: '8px',
                            borderBottomLeftRadius: isExpanded ? '0' : '8px'
                          }),
                          ...(idx === row.cells.length - 1 && {
                            pr: '24px',
                            borderRight: '1px solid',
                            borderTopRightRadius: '8px',
                            borderBottomRightRadius: isExpanded ? '0' : '8px'
                          })
                        }}
                      >
                        {cell.render('Cell')}
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* Footer Row */}
                  {renderExpandedRow && isExpanded && (
                    <TableRow
                      sx={{
                        pl: '0px !important',
                        marginTop: '-8px !important',
                        transform: 'translateY(-8px)',
                        backgroundColor: theme.palette.background.default,
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05) !important'
                              : `${alpha(theme.palette.primary[50] as string, 0.6)} !important`
                        }
                      }}
                    >
                      <TableCell
                        colSpan={columns.length}
                        sx={{
                          pt: 0,
                          borderBottomLeftRadius: '8px',
                          borderBottomRightRadius: '8px'
                        }}
                      >
                        <Collapse
                          onMouseEnter={() => setChildRowHovered(true)}
                          onMouseLeave={() => setChildRowHovered(false)}
                          in={isExpanded}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            sx={{
                              width: '100%',
                              p: expandedRowPadding,
                              boxSizing: 'border-box'
                            }}
                          >
                            {renderExpandedRow(row.original)}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <EmptyTable msg="No Data" colSpan={columns?.length} />
          )}

          {footer && (
            <TableRow
              sx={{
                backgroundColor: theme.palette.grey[700],
                '& td': {
                  color: 'white',
                  fontWeight: 600,
                  border: 'none',
                },
                ...footerSx,
              }}
            >
              {columns.map((col, idx) => (
                <TableCell
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: textSizeByBreakpoint(14),
                    border: 'none',
                    paddingX: '12px',
                    paddingY: sizeByBreakpoint(1, 12),
                    backgroundColor: theme.palette.grey[700],
                    textAlign: 'left',
                    ...(idx === 0 && {
                      borderTopLeftRadius: '10px',
                      borderBottomLeftRadius: '10px',
                      pl: '16px'
                    }),
                    ...(idx === columns.length - 1 && {
                      borderTopRightRadius: '10px',
                      borderBottomRightRadius: '10px',
                      pr: '24px'
                    }),
                    ...fullFooterCellSx,
                    ...footerCellSxByColumn?.[col.accessor as string],
                  }}
                  key={String(col.accessor ?? col.id)}
                >
                  {typeof footer === 'object' ? footer[col.accessor as string] ?? '' : footer}
                </TableCell>
              ))}
            </TableRow>
          )}

          {/* Bottom Pagination (inside table - only when not scrollable) */}
          {shouldShowPagination && !scrollableBody && (
            <TableRow
              sx={{
                backgroundColor: 'transparent !important',
                '& td': {
                  border: 'none !important',
                  backgroundColor: 'transparent !important',
                  p: '0 !important',
                  pt: '16px !important'
                }
              }}
            >
              <TableCell colSpan={columns?.length}>
                <TablePagination
                  handlePageSize={handlePageSize}
                  gotoPage={handlePageChange}
                  rows={rows}
                  setPageSize={() => {}}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  dataCount={dataCount}
                  hidePagePerRow={hidePagePerRow}
                  cursorBased={cursorBased}
                  onGotoFirst={onGotoFirst}
                />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      </Box>

      {/* Bottom Pagination (outside table - when scrollable body is enabled) */}
      {shouldShowPagination && scrollableBody && (
        <Box sx={{ pt: 2, flexShrink: 0 }}>
          <TablePagination
            handlePageSize={handlePageSize}
            gotoPage={handlePageChange}
            rows={rows}
            setPageSize={() => {}}
            pageIndex={pageIndex}
            pageSize={pageSize}
            dataCount={dataCount}
            hidePagePerRow={hidePagePerRow}
            cursorBased={cursorBased}
            onGotoFirst={onGotoFirst}
          />
        </Box>
      )}
    </Stack>
  );
};

export default ReactTable;
